import { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/DriveFileRenameOutline';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { RegistryRow } from 'shared';
import { api } from '../api';
import { LabeledField } from './LabeledField';
import { Note } from './Note';
import { DISPLAY, MONO, tokens } from '../theme';

function RenameDialog({ row, onClose }: { row: RegistryRow | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState('');

  const rename = useMutation({
    mutationFn: () => api.updatePage(row!.slug, { name: name.trim() }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['registry'] });
      void qc.invalidateQueries({ queryKey: ['page', row!.slug] });
      close();
    },
  });

  function close() {
    setName('');
    rename.reset();
    onClose();
  }

  const canSubmit = name.trim().length > 0 && name.trim() !== row?.code && !rename.isPending;

  return (
    <Dialog
      open={!!row}
      onClose={rename.isPending ? undefined : close}
      fullWidth
      maxWidth="xs"
      // Hộp thoại mở từ trong một dòng bấm được — không chặn thì mỗi lần bấm bên trong
      // hộp thoại lại kích luôn việc mở trang chi tiết.
      onClick={(e) => e.stopPropagation()}
    >
      <DialogTitle sx={{ fontFamily: DISPLAY, fontSize: 19, fontWeight: 600, color: tokens.ink }}>
        Đổi tên gọi
      </DialogTitle>
      <DialogContent>
        <Stack
          component="form"
          spacing={2}
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit) rename.mutate();
          }}
        >
          <LabeledField
            label="Tên gọi"
            autoFocus
            value={name}
            placeholder={row?.code}
            onChange={(e) => setName(e.target.value)}
            helperText="Chỉ đổi tên hiển thị. Địa chỉ trang và lịch sử điểm không đổi."
          />
          <Box>
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>Địa chỉ trang</Typography>
            <Typography sx={{ fontFamily: MONO, fontSize: 12.5, wordBreak: 'break-all' }}>
              {row?.publicUrl}
            </Typography>
          </Box>
          {rename.isError && <Note tone="bad">{(rename.error as Error).message}</Note>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={close} color="inherit" disabled={rename.isPending}>
          Huỷ
        </Button>
        <Button variant="contained" onClick={() => rename.mutate()} disabled={!canSubmit}>
          Lưu
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function DeleteDialog({ row, onClose }: { row: RegistryRow | null; onClose: () => void }) {
  const qc = useQueryClient();
  const del = useMutation({
    mutationFn: () => api.deletePage(row!.slug),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['registry'] });
      qc.removeQueries({ queryKey: ['page', row!.slug] });
      onClose();
    },
  });

  return (
    <Dialog
      open={!!row}
      onClose={del.isPending ? undefined : onClose}
      fullWidth
      maxWidth="xs"
      onClick={(e) => e.stopPropagation()}
    >
      <DialogTitle sx={{ fontFamily: DISPLAY, fontSize: 19, fontWeight: 600, color: tokens.ink }}>
        Xoá trang khỏi danh sách?
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Box>
            <Typography sx={{ fontSize: 14.5, fontWeight: 600 }}>{row?.code}</Typography>
            <Typography sx={{ fontFamily: MONO, fontSize: 12.5, color: 'text.secondary', wordBreak: 'break-all' }}>
              {row?.publicUrl}
            </Typography>
          </Box>

          {/* Câu cũ ở đây nói "mất luôn N lần quét, không hoàn lại được" — SAI kể
              từ spec SC-13 §2.1: xoá giờ là chuyển vào thùng rác và không mất gì.
              Doạ người dùng bằng một hậu quả không xảy ra cũng tệ ngang việc giấu
              một hậu quả có thật. */}
          <Note>
            Trang chuyển vào <b>thùng rác</b>, giữ nguyên toàn bộ lịch sử điểm. Khôi phục lại
            được trong 30 ngày; sau đó hệ thống tự xoá hẳn.
          </Note>

          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
            Copilot không tác động gì tới trang thật trên internet — chỉ bỏ nó khỏi danh sách
            theo dõi.
          </Typography>

          {del.isError && <Note tone="bad">{(del.error as Error).message}</Note>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} color="inherit" disabled={del.isPending}>
          Huỷ
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={() => del.mutate()}
          disabled={del.isPending}
          startIcon={del.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {del.isPending ? 'Đang xoá…' : 'Xoá trang'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Menu ba chấm ở cuối mỗi dòng: đổi tên, xoá. */
export function RowActions({ row }: { row: RegistryRow }) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [renaming, setRenaming] = useState<RegistryRow | null>(null);
  const [deleting, setDeleting] = useState<RegistryRow | null>(null);

  /** Cả dòng là một nút mở trang chi tiết, nên mọi thao tác ở đây phải chặn nổi bọt. */
  const stop = (e: React.MouseEvent) => e.stopPropagation();

  return (
    <>
      <IconButton
        size="small"
        aria-label={`Thao tác với ${row.code}`}
        onClick={(e) => {
          stop(e);
          setAnchor(e.currentTarget);
        }}
      >
        <MoreVertIcon sx={{ fontSize: 18 }} />
      </IconButton>

      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={() => setAnchor(null)}
        onClick={stop}
        slotProps={{ paper: { sx: { minWidth: 180 } } }}
      >
        <MenuItem
          onClick={() => {
            setAnchor(null);
            setRenaming(row);
          }}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText slotProps={{ primary: { fontSize: 14 } }}>Đổi tên gọi</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setAnchor(null);
            setDeleting(row);
          }}
          sx={{ color: tokens.roseText }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" sx={{ color: tokens.roseText }} />
          </ListItemIcon>
          <ListItemText slotProps={{ primary: { fontSize: 14 } }}>Xoá trang</ListItemText>
        </MenuItem>
      </Menu>

      <RenameDialog row={renaming} onClose={() => setRenaming(null)} />
      <DeleteDialog row={deleting} onClose={() => setDeleting(null)} />
    </>
  );
}
